import { useCallback } from 'react';
import onboardingService from '../services/onboardingService';
import { mapStepErrors } from '../services/onboardingMappers';
import { getStepById, LAST_STEP_ID } from '../utils/onboardingSteps.config';
import { canClearCurrentStep } from '../utils/stepClear';
import { canNavigateToStep, clampStep } from '../utils/stepNavigation';
import { validateProfileSetup, validateStep } from '../utils/validation';

export function useWizardNavigation({
  sessionIdRef,
  draftRef,
  currentStepRef,
  currentStep,
  steps,
  draft,
  profileId,
  organisationId,
  setCurrentStep,
  setStepErrors,
  setSaveError,
  setIsSaving,
  setShowResumeBanner,
  syncUrl,
  flushStepDraft,
  hydrateFromSession,
  syncSessionAfterMutation,
  resetWizardSession,
  clearCurrentStep,
  clearSaveTimeout,
  clearSessionId,
}) {
  const goToStep = useCallback(
    (step) => {
      const clamped = clampStep(step);
      if (!canNavigateToStep(clamped, steps)) return;
      setCurrentStep(clamped);
      syncUrl(clamped);
      void onboardingService.patchSession(sessionIdRef.current, { current_step: clamped });
      setStepErrors({});
    },
    [sessionIdRef, setCurrentStep, setStepErrors, steps, syncUrl]
  );

  const validateCurrentStep = useCallback(() => {
    const errors = validateStep(currentStep, draft, { profileId });
    setStepErrors(errors);
    return Object.keys(errors).length === 0;
  }, [currentStep, draft, profileId, setStepErrors]);

  const createProfileNow = useCallback(async () => {
    if (!sessionIdRef.current) return false;

    const setupErrors = validateProfileSetup(draftRef.current);
    if (Object.keys(setupErrors).length > 0) {
      setStepErrors(setupErrors);
      return false;
    }

    setIsSaving(true);
    setSaveError(null);
    try {
      clearSaveTimeout();
      await flushStepDraft('profile_users', draftRef.current);

      const res = await onboardingService.createWizardProfile(sessionIdRef.current);
      if (!res.ok) {
        const mapped = mapStepErrors(res.data);
        setStepErrors(mapped);
        const first =
          mapped.organisation_id ||
          mapped.profile_name ||
          mapped.clone_from_profile_id ||
          mapped.objectPermissions ||
          mapped.generalPermissions ||
          mapped.profile ||
          mapped.clone_profile ||
          Object.values(mapped)[0];
        setSaveError(
          typeof first === 'string' ? first : 'Could not create profile. Check the form and try again.'
        );
        return false;
      }
      const payload = res.data;
      if (payload?.session) {
        hydrateFromSession(payload.session);
      } else {
        const refreshed = await onboardingService.getSession(sessionIdRef.current);
        if (refreshed.ok && refreshed.data) {
          hydrateFromSession(refreshed.data);
        }
      }
      setStepErrors((prev) => {
        const next = { ...prev };
        delete next.profile;
        return next;
      });
      return true;
    } catch {
      setSaveError('Something went wrong while creating the profile.');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [
    clearSaveTimeout,
    draftRef,
    flushStepDraft,
    hydrateFromSession,
    sessionIdRef,
    setIsSaving,
    setSaveError,
    setStepErrors,
  ]);

  const completeCurrentStep = useCallback(async () => {
    const config = getStepById(currentStepRef.current);
    if (!config || !sessionIdRef.current) return false;

    clearSaveTimeout();
    await flushStepDraft(config.key, draftRef.current);

    const validateRes = await onboardingService.validateStep(sessionIdRef.current, config.key);
    if (validateRes.ok && validateRes.data) {
      const v = validateRes.data;
      if (!v.valid) {
        setStepErrors(mapStepErrors(v.errors));
        return false;
      }
    }

    const completeRes = await onboardingService.completeStep(sessionIdRef.current, config.key);
    if (!completeRes.ok) {
      setStepErrors(mapStepErrors(completeRes.data));
      setSaveError('Could not complete this step. Check the form and try again.');
      return false;
    }

    const payload = completeRes.data;
    if (payload?.session) {
      syncSessionAfterMutation(payload.session);
    } else {
      const refreshed = await onboardingService.getSession(sessionIdRef.current);
      if (refreshed.ok && refreshed.data) {
        syncSessionAfterMutation(refreshed.data);
      }
    }

    return true;
  }, [
    clearSaveTimeout,
    currentStepRef,
    draftRef,
    flushStepDraft,
    sessionIdRef,
    setSaveError,
    setStepErrors,
    syncSessionAfterMutation,
  ]);

  const handleContinue = useCallback(async () => {
    if (!sessionIdRef.current) return false;
    if (!validateCurrentStep()) return false;

    const config = getStepById(currentStep);
    if (!config) return false;

    setIsSaving(true);
    setSaveError(null);

    try {
      const ok = await completeCurrentStep();
      if (!ok) return false;

      const nextStep = Math.min(currentStep + 1, LAST_STEP_ID);
      setCurrentStep(nextStep);
      syncUrl(nextStep);
      await onboardingService.patchSession(sessionIdRef.current, { current_step: nextStep });

      setStepErrors({});
      setShowResumeBanner(false);
      return true;
    } catch {
      setSaveError('Something went wrong. Please try again.');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [
    completeCurrentStep,
    currentStep,
    sessionIdRef,
    setCurrentStep,
    setIsSaving,
    setSaveError,
    setShowResumeBanner,
    setStepErrors,
    syncUrl,
    validateCurrentStep,
  ]);

  const handleGoLive = useCallback(async () => {
    if (!sessionIdRef.current || currentStep !== LAST_STEP_ID) return { ok: false };

    if (!validateCurrentStep()) return { ok: false };

    setIsSaving(true);
    setSaveError(null);

    try {
      const completed = await completeCurrentStep();
      if (!completed) return { ok: false };

      const res = await onboardingService.goLive(sessionIdRef.current);
      if (!res.ok) {
        const flat = mapStepErrors(res.data);
        const msg =
          flat.step ||
          (typeof res.data === 'string' ? res.data : null) ||
          Object.values(flat)[0] ||
          'Complete all required steps before going live.';
        setSaveError(msg);
        return { ok: false, message: msg };
      }

      clearSessionId();
      return { ok: true };
    } catch {
      setSaveError('Something went wrong while completing setup.');
      return { ok: false };
    } finally {
      setIsSaving(false);
    }
  }, [
    clearSessionId,
    completeCurrentStep,
    currentStep,
    sessionIdRef,
    setIsSaving,
    setSaveError,
    validateCurrentStep,
  ]);

  const handleBack = useCallback(() => {
    if (currentStep <= 1 || !sessionIdRef.current) return;
    const prev = currentStep - 1;
    const config = getStepById(currentStep);
    if (config) {
      void flushStepDraft(config.key, draftRef.current);
    }
    setCurrentStep(prev);
    syncUrl(prev);
    void onboardingService.patchSession(sessionIdRef.current, { current_step: prev });
    setStepErrors({});
  }, [currentStep, draftRef, flushStepDraft, sessionIdRef, setCurrentStep, setStepErrors, syncUrl]);

  const handleSkip = useCallback(async () => {
    const config = getStepById(currentStep);
    if (!config || config.required || !sessionIdRef.current) return false;

    setIsSaving(true);
    setSaveError(null);

    try {
      const res = await onboardingService.skipWizardStep(sessionIdRef.current, config.key);
      if (!res.ok || !res.data) {
        setSaveError('Could not skip this step. Please try again.');
        return false;
      }

      hydrateFromSession(res.data);
      const nextStep = clampStep(res.data.current_step ?? currentStep + 1);
      setCurrentStep(nextStep);
      syncUrl(nextStep);
      setStepErrors({});
      return true;
    } catch {
      setSaveError('Something went wrong while skipping this step.');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [
    currentStep,
    hydrateFromSession,
    sessionIdRef,
    setCurrentStep,
    setIsSaving,
    setSaveError,
    setStepErrors,
    syncUrl,
  ]);

  const handleClearStep = useCallback(async () => {
    if (
      !canClearCurrentStep({
        currentStepId: currentStep,
        steps,
        organisationId,
        profileId,
      })
    ) {
      return false;
    }

    setIsSaving(true);
    setSaveError(null);
    try {
      const result = await clearCurrentStep(currentStep);
      if (result.ok) {
        setStepErrors({});
      }
      return result.ok;
    } catch {
      setSaveError('Something went wrong while clearing this step.');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [
    clearCurrentStep,
    currentStep,
    organisationId,
    profileId,
    setIsSaving,
    setSaveError,
    setStepErrors,
    steps,
  ]);

  const handleStartOver = useCallback(async () => {
    setIsSaving(true);
    try {
      await resetWizardSession();
      setStepErrors({});
      setShowResumeBanner(false);
      syncUrl(1);
    } finally {
      setIsSaving(false);
    }
  }, [resetWizardSession, setIsSaving, setShowResumeBanner, setStepErrors, syncUrl]);

  return {
    goToStep,
    validateCurrentStep,
    createProfileNow,
    handleContinue,
    handleGoLive,
    handleBack,
    handleSkip,
    handleClearStep,
    handleStartOver,
  };
}
